import { defineComponent as _defineComponent } from 'vue'
import type { PropType } from 'vue';
import { datadogRum } from '@datadog/browser-rum';

import { AdvamConfig, Payload } from '@white-label-types/parking-checkout';
import { getAppPaymentMerchantId } from '@white-label-helper/get-app-payment-merchant-id';
import { useEventBus } from '@white-label-helper/shared-composables';


export default /*#__PURE__*/_defineComponent({
  __name: 'payment-gateway-advam',
  props: {
  paymentsConfig: {
    type: Object as PropType<AdvamConfig>,
    required: true,
  },
  paymentsConfigReceived: {
    type: Boolean,
    required: true,
  },
  totalAmount: {
    type: Number,
    required: true,
  },
  isFormInvalid: {
    type: Boolean,
    required: true,
  },
},
  emits: ["create-booking", "payment-completed", "error", "cancel-transaction", "show-technical-error", "show-validation-errors"],
  setup(__props, { expose, emit }: { emit: ({
  (e: 'create-booking', payload: Payload): void,
  (e: 'payment-completed', payload: Payload): void,
  (e: 'error'): void,
  (e: 'cancel-transaction'): void,
  (e: 'show-technical-error'): void,
  (e: 'show-validation-errors'): void,
}), expose: any, slots: any, attrs: any }) {

const props = __props;





const environment = process.env.NUXT_ENV_ENVIRONMENT;
const eventBus = useEventBus();

const subscribeToGlobalEvent = (eventName: string, handler: (...args: any[]) => void) => {
  eventBus.$on(eventName, handler);
};

const unsubscribeFromGlobalEvent = (eventName: string, handler: (...args: any[]) => void) => {
  eventBus.$off(eventName, handler);
}

/**
 * Triggers the submit button inside 'advam-ui' custom element.
 */
const submitForm = () => {
  // This button is located in the `advam-ui` Web Component
  const btn: HTMLButtonElement | null = document.querySelector('#advam-ui__submit');
  btn?.click();
}

/**
 * Handler for the 'pay-submit' event from advam-ui web component.
 * This is called before payment authorization to validate the cart/booking.
 *
 * Flow:
 * 1. Emit 'create-booking' event to trigger backend order creation
 * 2. Wait for 'booking-created' global event (max 10s)
 * 3. Call callback(true) to proceed with payment or callback(false) on failure
 */
const onPaySubmit = async (event: CustomEvent): Promise<void> => {
  const BOOKING_CREATION_TIMEOUT_MS = 20000;

  /** Payment methods that render their own pay button outside the main form submit flow */
  const WALLET_PAYMENT_METHODS = new Set(['googlePay', 'applePay'] as const);

  const { callback, data } = event.detail;

  // Apple Pay and Google Pay are separate buttons apart from the main "Pay Now" button,
  // so we need to check the form validity, show errors and reload the widget when those payment methods used.
  if (WALLET_PAYMENT_METHODS.has(data.data) && props.isFormInvalid) {
    emit('show-validation-errors');
    callback(false);
    return;
  }

  try {
    await new Promise<void>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      const handleComplete = (result: boolean) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        unsubscribeFromGlobalEvent('booking-created', handleComplete);
        if (result) {
          resolve();
        } else {
          reject(new Error('Booking creation failed'));
        }
      };

      subscribeToGlobalEvent('booking-created', handleComplete);

      timeoutId = setTimeout(() => {
        unsubscribeFromGlobalEvent('booking-created', handleComplete);
        reject(new Error('Timeout after 20 seconds waiting for booking creation'));
      }, BOOKING_CREATION_TIMEOUT_MS);

      const payload = {
        payment_method_id: props.paymentsConfig.sessionId,
        merchant_id: getAppPaymentMerchantId(),
      };

      emit('create-booking', payload);
    });

    callback(true);
  } catch (error: any) {
    console.error('Advam onPaySubmit Error', error);
    datadogRum.addError('Advam onPaySubmit Error', error);
    emit('show-technical-error');
    callback(false);
  }
};

/**
 * Handler for successful transactions
 * Called after Advam successfully authorizes the payment
 */
const onTransactionSuccess = (event: CustomEvent) => {
  emit('payment-completed', {
    payment_method_id: event.detail?.nonce ?? '',
    merchant_id: getAppPaymentMerchantId(),
  });
};

expose({
  submitForm
})

return { __sfc: true,props, emit, environment, eventBus, subscribeToGlobalEvent, unsubscribeFromGlobalEvent, submitForm, onPaySubmit, onTransactionSuccess }
}

})